import { useTranslation } from 'react-i18next';
import { Column } from 'react-table';

const columnDefinitions: Record<string, Column<Rule>> = {
    ruleGroup: {
        Header: 'Rule Group',
        id: 'virtualRuleGroup',
        accessor: (d) => {
            return `${d?.ruleGroup}`;
        },
        disableSortBy: true,
        width: 80
    },
    virtualRuleGroup: {
        Header: () => null,
        id: 'virtualRuleGroup',
        accessor: (d) => {
            return `${d?.virtualRuleGroup}`;
        },
        disableSortBy: true
    },
    modifiedFlag: {
        Header: '',
        accessor: 'modifiedFlag',
        renderType: 'modifiedIcon',
        width: 5,
        title: 'Modified',
        disableSortBy: true
    },
    ruleId: {
        Header: 'Rule ID',
        accessor: 'id',
        dataType: 'text',
        minWidth: 48,
        width: 40
    },
    inputDescription: {
        id: '_formatted.inputDescription',
        Header: 'Rule Description',
        dataType: 'text',
        width: 176,
        accessor: (d) => d?._formatted?.inputDescription
    },
    description: {
        id: 'description',
        Header: 'Rule Description',
        dataType: 'text',
        width: 160,
        accessor: 'description'
    },
    ruleType: {
        id: 'ruleType',
        Header: 'Rule Type',
        dataType: 'text',
        width: 60,
        accessor: 'ruleType',
        renderType: 'ruleType'
    },
    conclusion: {
        id: '_formatted.outputDescription',
        Header: 'Conclusion',
        renderType: 'conclusion',
        width: 40,
        minWidth: 80,
        dataType: 'conclusion',
        renderOptions: (d) => {
            return {
                score: d?.parameters.score,
                conclusionValues: d?._formatted?.conclusionValues
            };
        },
        preformattedValue: (d, valueToCompare?: string | number) =>
            typeof valueToCompare === 'number' && d?.outputDescription?.includes('score')
                ? d?.parameters.score
                : d?.outputDescription,
        accessor: (d) => d?._formatted?.outputDescription
    },
    lastModified: {
        Header: 'Last Modified',
        // id is used by the match sorter search
        id: '_formatted.createdAt',
        dataType: 'datetime',
        renderType: 'datetime',
        sortType: 'datetime',
        width: 40,
        minWidth: 88,
        // in order to use the datetime sort type, we need to provide a date object
        accessor: 'createdAt',
        preformattedValue: (d) => d.createdAt
    },
    status: {
        id: '_formatted.active',
        Header: 'Status',
        minWidth: 60,
        dataType: 'text',
        width: 40,
        // added by the transformer
        accessor: (d) => d?._formatted?.active
    },
    override: {
        id: 'override',
        Header: 'Override',
        renderType: 'badge',
        renderOptions: {
            type: 'basic'
        },
        accessor: 'override'
    },
    executionOrder: {
        id: 'priority',
        Header: 'Execution Order',
        dataType: 'number',
        accessor: 'priority',
        width: 73
    },
    order: {
        id: 'priority',
        Header: 'Order',
        dataType: 'number',
        accessor: 'priority',
        width: 64
    },
    allowOverrides: {
        id: 'allowOverridesString',
        Header: 'AllowTenantOverrides',
        minWidth: 80,
        dataType: 'text',
        width: 40,
        accessor: 'allowOverridesString'
    },
    hiddenFromTenant: {
        id: 'hiddenFromTenantString',
        Header: 'hideFromTenants',
        minWidth: 90,
        dataType: 'text',
        width: 40,
        accessor: 'hiddenFromTenantString'
    }
};

export type Args = {
    columns: (
        | 'modifiedFlag'
        | 'overrideGroup'
        | 'ruleGroup'
        | 'ruleId'
        | 'inputDescription'
        | 'description'
        | 'conclusion'
        | 'lastModified'
        | 'status'
        | 'override'
        | 'ruleType'
        | 'executionOrder'
        | 'order'
        | 'virtualRuleGroup'
        | 'allowOverrides'
        | 'hiddenFromTenant'
        | Column<Rule>
    )[];
    overrides?: Record<string, Column<Rule>>;
};

// instead of defining the exact same columns in 3 different locations (sandbox, deployed, revisions)
// use this file to get columns
const useGetRuleColumns = ({ columns, overrides }: Args) => {
    const { t } = useTranslation();
    return columns.map((col) => {
        const colDef = typeof col === 'string' ? columnDefinitions[col] : col;
        const overrideColDef = (typeof col === 'string' && overrides && overrides[col]) || {};
        const thisHeader = (overrideColDef as Column<Rule>)?.Header || colDef.Header;
        // translate only if thisHeader is a string Header
        const header = typeof thisHeader === 'string' && thisHeader !== '' ? t(thisHeader) : thisHeader;
        return {
            ...(colDef || colDef),
            ...(overrideColDef || {}),
            Header: header
        } as Column<Rule>;
    });
};

export default useGetRuleColumns;
